// Highlight palette for session cards, keyed by room (venue). palette[0] is the program
// guide's existing bright blue — kept as a Chakra token so it always matches the rest of the
// UI — and the remaining five are bold, saturated colors assigned programmatically to rooms
// for demo purposes until Emma's venue records return their own `color` value.
export const roomColorPalette = [
    "blue.500", // existing bright blue (#3b82f6)
    "#F03E3E", // red
    "#F76707", // orange
    "#2F9E44", // green
    "#7048E8", // violet
    "#E64980", // pink
]

export const defaultRoomColor = roomColorPalette[0]

// Base accent color used when color-coding is NOT "byRoom" (i.e. "byTrack" or "none"): card
// titles/type-tags/View-details, the selected-card border, and the detail panel's border, title,
// and section labels all fall back to this deep blue. The programGuide theme's accent defaults
// import it so the whole base look is driven from one place. Kept separate from the room palette so
// changing the palette can't shift this neutral/base appearance.
export const roomInactiveColor = "#040883"

// Emma/CMS venue colors sometimes arrive as bare hex ("F03E3E") without the leading "#",
// which Chakra silently renders as an invalid color. Prefix those; pass named tokens and
// rgb()/hsl() strings through untouched. Empty/whitespace/non-strings resolve to null.
function normalizeColor(value) {
    if (typeof value !== "string") return null
    const trimmed = value.trim()
    if (!trimmed) return null
    return /^[0-9a-f]{3,8}$/i.test(trimmed) ? `#${trimmed}` : trimmed
}

// Small deterministic string hash so venues missing from the color map still get a stable,
// distinct palette color instead of all collapsing onto the default.
function hashToIndex(key, mod) {
    const str = String(key ?? "")
    let h = 0
    for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) | 0
    return Math.abs(h) % mod
}

function flattenVenues(venues = []) {
    return venues.flatMap(v => [v, ...flattenVenues(v.children)])
}

// Build a { [venueId]: color } map from the nested Emma venue tree. A venue's own `color`
// (once Emma populates it) wins; otherwise leaf rooms are assigned palette colors in a stable
// id-sorted order so each room gets a consistent, distinct highlight for the demo.
export function buildRoomColorMap(venues = []) {
    const flat = flattenVenues(venues)
    const rooms = flat.filter(v => !v.children?.length)
    const map = {}

    const sorted = [...rooms].sort((a, b) =>
        String(a.id).localeCompare(String(b.id), undefined, { numeric: true }),
    )
    sorted.forEach((venue, i) => {
        map[venue.id] = normalizeColor(venue.color) || roomColorPalette[i % roomColorPalette.length]
    })

    // Preserve explicit colors on non-leaf (parent) venues too, in case a session points at one.
    flat.forEach(venue => {
        const color = normalizeColor(venue.color)
        if (color && !map[venue.id]) map[venue.id] = color
    })

    return map
}

// Resolve the highlight color for a session's venue. Order: an explicit color on the venue ref,
// then the pre-built map, then a deterministic hash fallback so venues absent from the in-use
// tree (see findVenueRecursive misses in agendaFilters) still get a distinct, stable color.
export function resolveRoomColor(colorMap, venue) {
    if (!venue) return defaultRoomColor
    return (
        normalizeColor(venue.color) ||
        colorMap?.[venue.id] ||
        roomColorPalette[hashToIndex(venue.id ?? venue.name, roomColorPalette.length)]
    )
}
